import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconChartBar, IconRocket, IconSearch } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Container, Heading, Text } from '../atomic';

/**
 * @typedef {Object} Step
 * @property {number} number - Step number
 * @property {string} title - Step title
 * @property {string} description - Step description
 * @property {React.ReactNode} icon - Step icon
 */

/**
 * @typedef {Object} ProductHowItWorksProps
 * @property {string} [sectionId] - Section ID for navigation
 * @property {string} [headline] - Section headline
 * @property {string} [subheadline] - Section subheadline
 * @property {Step[]} [steps] - Array of steps
 */

/**
 * Product How It Works Section
 * 3-step horizontal timeline with connecting lines — all steps visible,
 * first step emphasized with navy circle, others lighter.
 *
 * @param {ProductHowItWorksProps} props - Component props
 * @returns {JSX.Element} How it works section
 */
function ProductHowItWorks({
  sectionId = 'how-it-works',
  headline = 'Get Started in Minutes',
  subheadline = '',
  steps = [
    {
      number: 1,
      title: 'Discover',
      description:
        'We collect information on your current FP&A and Cloud Financial Management process. Data needs context.',
      icon: <IconSearch size={36} stroke={1.5} />,
    },
    {
      number: 2,
      title: 'Deploy',
      description:
        'Purchase EllaFin™ in the AWS Marketplace. Once registration is completed your tenant is created in minutes.',
      icon: <IconRocket size={36} stroke={1.5} />,
    },
    {
      number: 3,
      title: 'Visualize',
      description:
        'Current spend dashboards created in 24 hours. Custom agents and additional financial intelligence in 72 hours.',
      icon: <IconChartBar size={36} stroke={1.5} />,
    },
  ],
}) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  /** Circle colors: first step navy, others progressively lighter */
  const circleStyles = [
    {
      bg: theme.palette.primary.main,
      iconColor: '#ffffff',
      shadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
    },
    {
      bg: theme.palette.primary.main,
      iconColor: '#ffffff',
      shadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
    },
    {
      bg: theme.palette.primary.main,
      iconColor: '#ffffff',
      shadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.15)}`,
    },
  ];

  return (
    <Box
      component="section"
      id={sectionId}
      ref={sectionRef}
      aria-label="How it works"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#13152b',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Heading
            variant="h2"
            sx={{
              fontSize: { xs: '1.75rem', md: '2.5rem' },
              fontWeight: 700,
              color: '#ffffff',
              mb: 2,
            }}
          >
            {headline}
          </Heading>
          {subheadline && (
            <Text
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: 'rgba(255, 255, 255, 0.7)',
                maxWidth: '600px',
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              {subheadline}
            </Text>
          )}
        </Box>

        {/* Steps Timeline */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 5, md: 0 },
            justifyContent: 'center',
            alignItems: { xs: 'center', md: 'flex-start' },
            px: { xs: 2, md: 4 },
            position: 'relative',
          }}
        >
          {steps.map((step, index) => {
            const style = circleStyles[index] || circleStyles[2];
            return (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  position: 'relative',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  transition: 'all 0.6s ease',
                  transitionDelay: `${index * 150}ms`,
                  px: { xs: 1, md: 2 },
                }}
              >
                {/* Connector Line (between circles, not on last step) */}
                {index < steps.length - 1 && !isMobile && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '44px',
                      left: '50%',
                      width: '100%',
                      height: '3px',
                      backgroundColor: theme.palette.secondary.main,
                      zIndex: 0,
                    }}
                  />
                )}

                {/* Step Circle */}
                <Box
                  sx={{
                    width: { xs: '72px', md: '88px' },
                    height: { xs: '72px', md: '88px' },
                    borderRadius: '50%',
                    backgroundColor: style.bg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    position: 'relative',
                    zIndex: 1,
                    boxShadow: style.shadow,
                    color: style.iconColor,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.08)',
                    },
                  }}
                >
                  {step.icon}
                </Box>

                {/* Step Number Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: '-6px', md: '-8px' },
                    left: '50%',
                    transform: {
                      xs: 'translateX(18px)',
                      md: 'translateX(22px)',
                    },
                    width: { xs: '24px', md: '28px' },
                    height: { xs: '24px', md: '28px' },
                    borderRadius: '50%',
                    backgroundColor: theme.palette.secondary.main,
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    fontWeight: 700,
                    zIndex: 2,
                    border: '2px solid #13152b',
                  }}
                >
                  {step.number}
                </Box>

                {/* Step Title */}
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: '#ffffff',
                    mb: 1,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {step.title}
                </Typography>

                {/* Step Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.7,
                    maxWidth: '280px',
                    fontSize: { xs: '0.85rem', md: '0.95rem' },
                  }}
                >
                  {step.description}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
}

export default ProductHowItWorks;
